import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { Spinner } from "../ui";

const MIN_VISIBLE_MS = 300;
/** Let the next route mount and register queries before we treat fetches as idle. */
const MOUNT_SETTLE_MS = 48;

export function NavigationLoader() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const navKey = `${location.pathname}${location.search}`;
  const [visible, setVisible] = useState(false);
  const firstNav = useRef(true);
  const runId = useRef(0);

  useEffect(() => {
    if (firstNav.current) {
      firstNav.current = false;
      return;
    }

    const currentRun = ++runId.current;
    setVisible(true);
    const started = Date.now();

    let cancelled = false;
    let mountTimer: ReturnType<typeof setTimeout> | undefined;
    let minTimer: ReturnType<typeof setTimeout> | undefined;
    let pollTimer: ReturnType<typeof setTimeout> | undefined;

    function hideIfReady() {
      if (cancelled || currentRun !== runId.current) return;

      const elapsed = Date.now() - started;
      const fetching = queryClient.isFetching({ type: "active" }) > 0;

      if (fetching) {
        pollTimer = setTimeout(hideIfReady, 32);
        return;
      }

      if (elapsed < MIN_VISIBLE_MS) {
        minTimer = setTimeout(hideIfReady, MIN_VISIBLE_MS - elapsed);
        return;
      }

      setVisible(false);
    }

    mountTimer = setTimeout(hideIfReady, MOUNT_SETTLE_MS);

    return () => {
      cancelled = true;
      clearTimeout(mountTimer);
      clearTimeout(minTimer);
      clearTimeout(pollTimer);
    };
  }, [navKey, queryClient]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="navigation-loader"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="pointer-events-auto fixed inset-0 z-[75] flex items-center justify-center bg-brand-dark/85 backdrop-blur-sm"
          aria-live="polite"
          aria-busy="true"
          aria-label="Loading page"
        >
          <Spinner className="h-10 w-10" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
